/**
 * Centralised, timezone-aware date formatting utilities.
 *
 * Every display date in the app should go through these helpers so that
 * timestamps are always shown in the hotel property's timezone rather than
 * the browser's local timezone.
 *
 * The property timezone is stored in localStorage (key: 'propertyTimezone')
 * and kept in sync by App.js whenever the selected property changes.
 */

const FALLBACK_TZ = 'Asia/Kolkata';

/** Return the active property timezone. */
export function getPropertyTimezone() {
  return localStorage.getItem('propertyTimezone') || FALLBACK_TZ;
}

/** Set the active property timezone (called from App.js). */
export function setPropertyTimezone(tz) {
  localStorage.setItem('propertyTimezone', tz || FALLBACK_TZ);
}

// ── Display formatters ────────────────────────────────────────────────

/** Format as date only – "12/03/2026" (en-GB) */
export function fmtDate(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { timeZone: tz || getPropertyTimezone() });
}

/** Format as date+time – "12/03/2026, 2:30:00 pm" */
export function fmtDateTime(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', { hour12: true, timeZone: tz || getPropertyTimezone() });
}

/** Format as time only – "2:30 pm" */
export function fmtTime(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: tz || getPropertyTimezone() });
}

/** Format as "12 Mar 2026" */
export function fmtDateMedium(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz || getPropertyTimezone() });
}

/** Format as "12 Mar 2026, 2:30 pm" */
export function fmtDateTimeMedium(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: tz || getPropertyTimezone(),
  });
}

/** Format as "12 Mar, 2:30 pm" (short, no year) */
export function fmtDateTimeShort(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: tz || getPropertyTimezone(),
  });
}

// ── ISO / computation helpers ─────────────────────────────────────────

/** Today's date in property timezone as YYYY-MM-DD string. */
export function todayIso(tz) {
  const t = tz || getPropertyTimezone();
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: t, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  return parts; // en-CA gives YYYY-MM-DD
}

/** Convert any date value to YYYY-MM-DD in property timezone. */
export function toIsoDate(value, tz) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const t = tz || getPropertyTimezone();
  return new Intl.DateTimeFormat('en-CA', { timeZone: t, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Format as DD/MM/YYYY from ISO-like string, timezone-aware. */
export function fmtDateDMY(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10).split('-').reverse().join('/');
  const iso = toIsoDate(d, tz);
  return iso.split('-').reverse().join('/');
}

/** Format as DD-MM-YYYY from ISO-like string, timezone-aware. */
export function fmtDateDMYDash(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10).split('-').reverse().join('-');
  const iso = toIsoDate(d, tz);
  return iso.split('-').reverse().join('-');
}

/** Extract HH:mm from a timestamp, timezone-aware. */
export function fmtTimeHHMM(value, tz) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(11, 16);
  const t = tz || getPropertyTimezone();
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: t });
}

/** Format for datetime-local input value (YYYY-MM-DDTHH:mm) in property tz. */
export function toDateTimeLocalValue(value, tz) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const t = tz || getPropertyTimezone();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: t,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type) => (parts.find(p => p.type === type) || {}).value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Convert a naive datetime-local string ("YYYY-MM-DDTHH:mm") to a UTC ISO
 * string, interpreting the wall-clock time in the property's timezone.
 *
 * Why this exists: HTML <input type="datetime-local"> produces strings with
 * NO timezone suffix. Sending them to the backend lets each layer guess —
 * Rust core treats them as UTC, JS Date treats them as server-local. Both
 * are wrong for a multi-timezone-capable system. This helper resolves the
 * ambiguity on the client side, where we already know the property TZ.
 *
 * If the input already has a TZ marker (Z, +HH:MM, -HH:MM), it's normalized
 * via Date and returned as UTC ISO unchanged.
 *
 * @param {string} value  e.g. "2026-06-01T10:53"
 * @param {string} [tz]   e.g. "Asia/Kolkata" — defaults to property tz
 * @returns {string}      UTC ISO "2026-06-01T05:23:00.000Z" — or '' if invalid
 */
export function dateTimeLocalToIso(value, tz) {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';
  // Already has a TZ marker — let Date parse it and return canonical UTC
  if (/[Zz]$|[+\-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  // Naive "YYYY-MM-DDTHH:mm[:ss]" — interpret wall-clock in property TZ
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  const [, Y, Mo, D, H, Mi, S] = m;
  const Y_ = Number(Y), Mo_ = Number(Mo), D_ = Number(D);
  const H_ = Number(H), Mi_ = Number(Mi), S_ = Number(S || 0);
  const t = tz || getPropertyTimezone();
  // Compute the actual UTC instant such that, when rendered in tz `t`,
  // it produces wall-clock (Y_, Mo_, D_, H_, Mi_, S_).
  // First guess: pretend the wall-clock is UTC.
  const guessUtcMs = Date.UTC(Y_, Mo_ - 1, D_, H_, Mi_, S_, 0);
  // Find what `t` reads at that moment, compute the offset, then correct.
  const offsetMin = tzOffsetMinutesAt(t, new Date(guessUtcMs));
  const trueUtcMs = guessUtcMs - offsetMin * 60_000;
  return new Date(trueUtcMs).toISOString();
}

/**
 * Returns the offset of timezone `tz` from UTC at the given instant,
 * in minutes east of UTC (positive for IST = +330).
 */
function tzOffsetMinutesAt(tz, instant) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const parts = dtf.formatToParts(instant);
  const get = (type) => Number((parts.find(p => p.type === type) || {}).value || 0);
  let hour = get('hour'); if (hour === 24) hour = 0;
  const wallMs = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'), 0);
  return Math.round((wallMs - instant.getTime()) / 60_000);
}

/** Format as short weekday name ("Mon", "Tue") */
export function fmtWeekdayShort(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: tz || getPropertyTimezone() });
}

/** Format as "DD Mon" (e.g. "12 Mar") */
export function fmtDateDayMonth(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: tz || getPropertyTimezone() });
}

/** Format as "DD-Mon-YY" (e.g. "12-Mar-26") */
export function fmtDateShortYear(value, tz) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: tz || getPropertyTimezone() }).replace(/ /g, '-');
}

/**
 * Keep a From/To date pair consistent after one end is edited.
 *
 * An inverted range (From later than To) is never what the operator meant — the
 * screen just silently returns "no rows". Rather than validating and scolding,
 * we drag the other end along: pick a From after the To and the To follows;
 * pick a To before the From and the From follows. The range is therefore always
 * at least the single day just picked.
 *
 * Object-state screens:
 *   setFilters(p => syncDateRange({ ...p, from_date: v }, 'from_date', 'to_date', 'from_date'))
 * Two-useState screens: compare inline — this helper is for the object shape.
 *
 * Dates are ISO 'YYYY-MM-DD' strings, so plain string comparison is ordering-safe.
 */
export function syncDateRange(state, fromKey, toKey, changedKey) {
  const from = state?.[fromKey];
  const to = state?.[toKey];
  if (!from || !to || from <= to) return state;
  return changedKey === fromKey
    ? { ...state, [toKey]: from }
    : { ...state, [fromKey]: to };
}
